import type { Biller } from 'app-components/flux/bill-presentment/billPresentmentTypes';
import type { Institution } from 'app-components/flux/institutions/institutionsTypes';

/**
 * Illustration names shared between web (QIllustration) and mobile (SIllustration).
 * Adding a new name here will produce TypeScript errors in both platforms until handled.
 */
export type InstitutionIllustrationName = 'zillowProperty' | 'kbbVehicle';

/**
 * Discriminated union describing WHAT icon to display for an institution login.
 * Each platform maps these variants to its own rendering primitives:
 *   - illustration → QIllustration (web) / SIllustration (mobile)
 *   - logoUrl      → <img> (web) / <Image> or <SvgCssUri> (mobile)
 *   - base64       → <img> with data URI (web) / <Image> with data URI (mobile)
 *   - fallback     → platform-specific bank building asset
 */
export type InstitutionIconSource =
  | { kind: 'illustration'; illustrationName: InstitutionIllustrationName }
  | { kind: 'logoUrl'; url: string; name: string; logoStyle: string | null; isStripe: boolean }
  | { kind: 'base64'; data: string; name: string; logoStyle: string | null }
  | { kind: 'fallback'; logoStyle: null };

/**
 * Discriminated input for the `getInstitutionIconSource` selector.
 *   - `{ accountId }` — resolve icon from account type + its institution login
 *   - `{ institutionLoginId }` — resolve icon from institution login (channel + institution)
 *   - `{ institution? }` — resolve directly from an institution/biller object
 */
export type InstitutionIconInput =
  | { accountId: string } // usually you want to pass this when there is an account
  | { institutionLoginId: string } // use this when you have an institution login with no guarantee of a valid institution (ex. kbb and zillow)
  | { institution?: ResolveIconInput | null }; // only use this when you know you have a valid institution

/** Extract the logoStyle from an InstitutionIconSource (null for illustration/fallback kinds). */
export const getLogoStyle = (source: InstitutionIconSource): string | null =>
  source.kind === 'logoUrl' || source.kind === 'base64' ? source.logoStyle : null;

/**
 * Accepted input for the pure resolution utility — either an Institution (or a Pick of it) or a Biller.
 */
export type ResolveIconInput = Pick<Institution, 'logoUrl' | 'logo' | 'logoStyle' | 'name'> | Biller;

type IllustrationIconSource = Extract<InstitutionIconSource, { kind: 'illustration' }>;

/** Single source of truth for account types that resolve to illustration icons. */
const ACCOUNT_TYPE_ILLUSTRATIONS: Partial<Record<string, IllustrationIconSource>> = {
  VEHICLE: { kind: 'illustration', illustrationName: 'kbbVehicle' },
  REAL_ESTATE: { kind: 'illustration', illustrationName: 'zillowProperty' },
};

export const getIconForAccountType = (accountType: string | undefined): IllustrationIconSource | undefined =>
  ACCOUNT_TYPE_ILLUSTRATIONS[accountType ?? ''];
